/**
 * ReservationListView — main list panel for the Reservation Management feature.
 *
 * Props-only component: receives all data and callbacks from the parent.
 * No data fetching, no routing. Renders FilterPanel + table + pagination.
 *
 * Layout:
 *   - Horizontal-scroll container with sticky right actions column.
 *   - z-index layers: header row z-20, corner header cell z-30, body sticky cells z-10.
 *
 * Consumed by: apps/mockups (via fake adapters), later by the real apps/web route.
 */

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { FilterPanel } from '@ooh/ui/components/filter-panel';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@ooh/ui/components/table';
import { cn } from '@ooh/ui/lib/utils';
import { ReservationListRow } from './reservation-list-row';
import { ScopeTogglePill, SortIcon, STICKY_COL_SHADOW } from './reservation-list-cells';
import type { ReservationListViewProps } from './reservation-list-view-types';
export type {
  ReservationListItem,
  ReservationListViewLabels,
  ReservationListViewPermissions,
  ReservationListViewProps,
} from './reservation-list-view-types';

// ─── Component ───────────────────────────────────────────────

// eslint-disable-next-line complexity -- cohesive table renderer; complexity is the optional-affordance fan-out (two scope toggles + waitlist expansion), same shape as UnitReservationsTab
export function ReservationListView({
  items,
  filterFields,
  filterValues,
  filterLabels,
  onFilterChange,
  sortBy,
  sortDir,
  onSort,
  sortLabel,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onAction,
  onBlockAvailability,
  permissions,
  labels,
  currentUserId,
  myReservationsActive = false,
  onToggleMyReservations,
  myReservationsLabel,
  queueByReservationId,
  queueLabels,
  onQueueAction,
  initialExpandedQueueId = null,
  hasQueueActive = false,
  onToggleHasQueue,
  hasQueueLabel,
}: ReservationListViewProps) {
  const totalPages = Math.ceil(totalCount / pageSize);
  const showMyReservationsToggle = onToggleMyReservations != null && myReservationsLabel != null;
  const showHasQueueToggle = onToggleHasQueue != null && hasQueueLabel != null;

  // Deep links (?expand=<id>, "Otwórz pełną kolejkę", clicking a waiting count
  // elsewhere) land with one row already open, so the user sees the line they
  // came for instead of a collapsed count they have to hunt for.
  const [expandedQueues, setExpandedQueues] = React.useState<ReadonlySet<number>>(() =>
    initialExpandedQueueId !== null ? new Set([initialExpandedQueueId]) : new Set(),
  );

  const toggleQueue = (reservationId: number) => {
    setExpandedQueues((prev) => {
      const next = new Set(prev);
      if (next.has(reservationId)) next.delete(reservationId);
      else next.add(reservationId);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ─── Filter panel ──────────────────────────── */}
      <div data-testid="reservation-filter-panel" className="shrink-0 px-6 pt-4 pb-3">
        <FilterPanel
          fields={filterFields}
          value={filterValues}
          onChange={onFilterChange}
          labels={filterLabels}
          leadingContent={
            showMyReservationsToggle || showHasQueueToggle ? (
              <>
                {showMyReservationsToggle ? (
                  <ScopeTogglePill
                    label={myReservationsLabel ?? ''}
                    active={myReservationsActive}
                    testId="reservation-filter-my-reservations"
                    onToggle={() => onToggleMyReservations?.()}
                  />
                ) : null}
                {showHasQueueToggle ? (
                  <ScopeTogglePill
                    label={hasQueueLabel ?? ''}
                    active={hasQueueActive}
                    testId="reservation-filter-has-queue"
                    onToggle={() => onToggleHasQueue?.()}
                  />
                ) : null}
              </>
            ) : undefined
          }
        />
        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span>{labels.totalCountLabel(totalCount)}</span>
          <span className="text-2xs">{sortLabel}</span>
        </div>
      </div>

      {/* ─── Data table with horizontal scroll + sticky actions ── */}
      <div className="flex-1 overflow-hidden px-6">
        <div
          data-testid="reservation-data-table"
          className="overflow-x-auto overflow-y-auto rounded-lg border border-border bg-background h-full"
          style={{ height: 'calc(100vh - 280px)' }}
        >
          <Table className="text-[13px] min-w-[900px]">
            <TableHeader className="sticky top-0 z-20 bg-background">
              <TableRow className="border-b border-border">
                <TableHead
                  className="cursor-pointer select-none w-[180px] min-w-[180px]"
                  // eslint-disable-next-line i18next/no-literal-string -- sort field key, not user-facing
                  onClick={() => onSort('status')}
                >
                  {labels.statusLabel}
                  <SortIcon active={sortBy === 'status'} dir={sortDir} />
                </TableHead>
                <TableHead className="w-[210px] min-w-[180px]">{labels.mediaUnitLabel}</TableHead>
                <TableHead
                  className="cursor-pointer select-none w-[100px] min-w-[90px]"
                  // eslint-disable-next-line i18next/no-literal-string -- sort field key, not user-facing
                  onClick={() => onSort('date_from')}
                >
                  {labels.dateFromLabel}
                  <SortIcon active={sortBy === 'date_from'} dir={sortDir} />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none w-[100px] min-w-[90px]"
                  // eslint-disable-next-line i18next/no-literal-string -- sort field key, not user-facing
                  onClick={() => onSort('date_to')}
                >
                  {labels.dateToLabel}
                  <SortIcon active={sortBy === 'date_to'} dir={sortDir} />
                </TableHead>
                {/* Expiry deadline — a separate column from the campaign window
                    (Od/Do) above: the campaign window states which dates the
                    unit is taken, the deadline states when the RESERVATION
                    ITSELF lapses and is released. Display-only, not sortable
                    (reservation-panel spec). Same abbreviated-header-plus-
                    tooltip pattern as Kolejka below. */}
                <TableHead className="w-32 min-w-28" title={labels.deadlineTooltipLabel}>
                  {labels.deadlineLabel}
                </TableHead>
                {/* Kolejka sits next to the term, NOT last: as the final
                    non-sticky column it rendered underneath the sticky actions
                    column at the default (unscrolled) position, so the
                    waiting-count chip was invisible until you scrolled right —
                    fatal now that the chip is the entry point to the queue
                    overview (CU-86cb7ctfw). It also reads better here: the
                    queue belongs to the unit+term, which is the pair of
                    columns immediately to its left. */}
                <TableHead
                  className="w-[86px] min-w-[86px] whitespace-nowrap text-center"
                  title={labels.queuedTooltipLabel}
                >
                  {labels.queuedLabel}
                </TableHead>
                <TableHead className="w-[150px] min-w-[130px]">{labels.holderLabel}</TableHead>
                <TableHead className="w-[190px] min-w-[140px]">{labels.offerLabel}</TableHead>
                <TableHead className="w-[140px] min-w-[120px]">{labels.sourceLabel}</TableHead>
                {/* Sticky corner: z-30 beats all body cells */}
                <TableHead
                  className={cn(
                    'sticky right-0 bg-background z-30 w-[52px] min-w-[52px] text-center',
                    STICKY_COL_SHADOW,
                  )}
                >
                  {labels.actionsLabel}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, idx) => (
                <ReservationListRow
                  key={item.id}
                  item={item}
                  index={idx}
                  permissions={permissions}
                  labels={labels}
                  currentUserId={currentUserId}
                  queueEntries={
                    queueLabels === undefined ? [] : (queueByReservationId?.[item.id] ?? [])
                  }
                  queueLabels={queueLabels}
                  isQueueOpen={expandedQueues.has(item.id)}
                  onToggleQueue={toggleQueue}
                  onAction={onAction}
                  onBlockAvailability={onBlockAvailability}
                  onQueueAction={onQueueAction}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ─── Pagination ────────────────────────────── */}
      <div className="shrink-0 flex items-center justify-between px-6 py-3 border-t border-border/40 bg-background">
        <span className="text-[13px] text-muted-foreground">
          {labels.pageLabel(page, totalPages)}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label={labels.prevPageLabel}
          >
            <ChevronLeft className="h-4 w-4" />
            {labels.prevPageLabel}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label={labels.nextPageLabel}
          >
            {labels.nextPageLabel}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
